<template>
  <div
    v-bind="$attrs"
    :class="[
      style.namedValue,
      {
        [style.filled]: isFilled,
        [style.disabled]: disabled,
        [style.error]: error
      }
    ]"
    :aria-disabled="disabled ? 'true' : 'false'"
    :aria-invalid="error ? 'true' : 'false'"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <button
      :class="style.dragButton"
      type="button"
      :disabled="disabled"
      :aria-label="dragAriaLabel"
      @pointerdown="onDragStart"
    >
      <IconBase :icon="dragIcon" tone="tertiary" />
    </button>

    <div :class="style.inputs">
      <div :class="[style.field, style.name]">
        <input
          :class="style.input"
          type="text"
          :value="name"
          :placeholder="namePlaceholder"
          :disabled="disabled"
          :readonly="readonly"
          :aria-invalid="error ? 'true' : 'false'"
          autocomplete="off"
          @input="onNameInput"
          @change="onNameChange"
        />
      </div>

      <div :class="[style.field, style.value]" v-if="valuePlaceholder">
        <input
          :class="style.input"
          type="text"
          :value="value"
          :placeholder="valuePlaceholder"
          :disabled="disabled"
          :readonly="readonly"
          :aria-invalid="error ? 'true' : 'false'"
          autocomplete="off"
          @input="onValueInput"
          @change="onValueChange"
        />

        <span v-if="suffix" :class="style.suffix">
          {{ suffix }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import style from "./InputNamedValue.module.scss";
import IconBase from "@/elements/UI/icon/IconBase.vue";
import dragIcon from "$images/icons/tabs/Name=Drag-vertical, Size=16.svg";

export default defineComponent({
  name: "InputNamedValue",
  components: {IconBase},
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      default: ""
    },
    value: {
      type: String,
      default: ""
    },
    namePlaceholder: {
      type: String,
      default: "Текст"
    },
    valuePlaceholder: {
      type: String,
      default: ""
    },
    suffix: {
      type: String,
      default: "₽"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    dragAriaLabel: {
      type: String,
      default: "Перетащить"
    }
  },
  emits: [
    "update:name",
    "update:value",
    "change:name",
    "change:value",
    "focus",
    "blur",
    "drag-start"
  ],
  data() {
    return {
      style,
      dragIcon
    };
  },
  computed: {
    isFilled(): boolean {
      return Boolean(this.name.trim() || this.value.trim());
    }
  },
  methods: {
    onNameInput(event: Event): void {
      const target = event.target as HTMLInputElement;
      this.$emit("update:name", target.value);
    },

    onValueInput(event: Event): void {
      const target = event.target as HTMLInputElement;
      this.$emit("update:value", target.value);
    },

    onNameChange(event: Event): void {
      const target = event.target as HTMLInputElement;
      this.$emit("change:name", target.value);
    },

    onValueChange(event: Event): void {
      const target = event.target as HTMLInputElement;
      this.$emit("change:value", target.value);
    },

    onFocusIn(event: FocusEvent): void {
      this.$emit("focus", event);
    },

    onFocusOut(event: FocusEvent): void {
      const currentTarget = event.currentTarget as HTMLElement;
      const relatedTarget = event.relatedTarget as Node | null;

      if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
        this.$emit("blur", event);
      }
    },

    onDragStart(event: PointerEvent): void {
      if (this.disabled) {
        return;
      }

      this.$emit("drag-start", event);
    }
  }
});
</script>
